import {
  Accordion,
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverRootProps,
  Portal,
  useAccordionItemContext,
} from '@chakra-ui/react';
import React, { FC, useState } from 'react';
import {
  MdArrowDropDown,
  MdArrowDropUp,
  MdClose,
  MdFilterList,
} from 'react-icons/md';
import useOutsideClick from '../../hooks/useOutsideClick';
import { Tooltip } from '../ui/tooltip';
import MAccordionPanel from './MAccordionPanel';
import MButton from './MButton';
import MCheckbox from './MCheckbox';
import MCustomBadge from './MCustomBadge';
import MCustomIconButton from './MCustomIconButton';
import MText from './MText';

interface MFilterCardProps {
  onClose?: null | ((applyFilters: boolean) => void);
  onResetFilter?: null | (() => void);
  children?: any | null;
  renderTrigger?:
    | null
    | (({ onOpen }: { onOpen: () => void }) => React.ReactElement);
  filtersApplied?: number;
  filterTooltip?: string;
  popoverProps?: Omit<PopoverRootProps, 'children'>;
  isInternalFilterUnchanged: boolean;
}

const MFilterCard: FC<MFilterCardProps> = React.forwardRef<
  any,
  MFilterCardProps
>(
  (
    {
      onClose,
      onResetFilter,
      children,
      renderTrigger,
      filtersApplied,
      filterTooltip = 'Filter',
      popoverProps,
      isInternalFilterUnchanged,
    }: MFilterCardProps,
    ref,
  ) => {
    const applyButtonRef = React.useRef<HTMLButtonElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const filterContainerRef = React.useRef<HTMLDivElement>(null);

    const initialFocusRef = !isInternalFilterUnchanged
      ? applyButtonRef
      : contentRef;

    const [isOpen, setIsOpen] = useState(false);
    useOutsideClick({
      ref: filterContainerRef,
      // Content is portaled to <body>, so it's no longer a DOM descendant of
      // filterContainerRef — exclude clicks inside it separately, or every
      // click in the open filter panel would count as "outside" and close it.
      handler: (event) => {
        const target = event.target as Node;
        if (contentRef.current?.contains(target)) {
          return;
        }
        setIsOpen(false);
      },
    });

    const handleOpen = () => {
      setIsOpen(!isOpen);
      if (isOpen) {
        initialFocusRef?.current?.focus();
      }
    };

    const handleResetFilter = () => {
      setIsOpen(false);
      onResetFilter && onResetFilter();
    };

    const handleApplyFilter = () => {
      setIsOpen(false);
      onClose && onClose(true);
    };

    const handleClose = () => {
      setIsOpen(false);
      onClose && onClose(false);
    };

    return (
      <Box ref={filterContainerRef} position="relative" maxWidth="300px">
        <Popover.Root
          lazyMount
          unmountOnExit
          initialFocusEl={() => initialFocusRef.current}
          autoFocus
          open={isOpen}
          {...popoverProps}
          onOpenChange={(e) => {
            if (e.open) {
              handleOpen();
            } else {
              handleClose();
            }
          }}
          positioning={{
            placement: 'bottom-end',
            offset: { mainAxis: 0, crossAxis: 0 },
            // Content is portaled to <body>, so `strategy: 'absolute'` (the
            // default) has no positioned ancestor to compute offsets
            // against. `'fixed'` positions relative to the viewport
            // instead, which stays correct across a portal boundary.
            strategy: 'fixed',
          }}
        >
          <Popover.Trigger asChild>
            {renderTrigger ? (
              renderTrigger({ onOpen: handleOpen })
            ) : (
              <MCustomBadge
                text={(filtersApplied || '') as string}
                tooltipLabel="Reset All Filters"
                onClickReset={handleResetFilter}
              >
                <Tooltip
                  content={filterTooltip}
                  positioning={{
                    placement: 'bottom-end',
                  }}
                >
                  <MCustomIconButton
                    data-testid="filter-card-button"
                    containerSize="8"
                    btnSize="6"
                    onClick={handleOpen}
                    icon={MdFilterList}
                    aria-label="Filter"
                    variant="icon"
                    mb="0"
                  />
                </Tooltip>
              </MCustomBadge>
            )}
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content
                // Border, radius, and shadow inherit from the Popover theme
                // (8px radius, border.subtle, shadow.md). Keeps filter dropdowns
                // visually consistent with menus and other popovers.
                width="300px"
                position="relative"
                ref={contentRef}
              >
                <Popover.CloseTrigger aria-label="Close">
                  <Icon asChild>
                    <MdClose />
                  </Icon>
                </Popover.CloseTrigger>
                <Popover.Header>Filter</Popover.Header>
                <Box
                  p={4}
                  maxH="50vh"
                  overflowY="auto"
                  mb="64px"
                  className="custom-scroll-bar-v1"
                >
                  <Accordion.Root
                    multiple
                    defaultValue={['0']}
                    variant="default"
                  >
                    {children}
                  </Accordion.Root>
                </Box>
                <Flex
                  width="100%"
                  padding={4}
                  margin="auto"
                  // Absolute (not fixed) so this pins to the bottom of the
                  // 300px content box (which is `position: relative`),
                  // not the viewport.
                  position="absolute"
                  borderTop="1px solid"
                  borderTopColor="tGray.back"
                  style={{ bottom: 0 }}
                  justifyContent="space-between"
                >
                  <Button variant="cancel" mr="2" onClick={handleResetFilter}>
                    Reset All
                  </Button>
                  <Button
                    ref={applyButtonRef}
                    variant="primary"
                    onClick={handleApplyFilter}
                    disabled={isInternalFilterUnchanged}
                  >
                    Apply
                  </Button>
                </Flex>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      </Box>
    );
  },
);

interface FilterCardAccordionItemBodyProps {
  title: string;
  filterApplied?: boolean;
  handleClearFilter: () => void;
  children: any;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const FilterCardAccordionItemBody = ({
  title,
  filterApplied = false,
  handleClearFilter,
  children,
}: FilterCardAccordionItemBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <Accordion.ItemTrigger
        data-testid={`${title}-accordion-button`}
        p="0"
        _hover={{ background: 'none' }}
        my="2"
        justifyContent="space-between"
      >
        <Box flex="1" textAlign="left">
          <MText fontWeight="bold">{title}</MText>
        </Box>
        <Flex alignItems="center">
          {filterApplied && (
            // Rendered as a span (not a button) because it sits inside the
            // AccordionButton — nesting <button> inside <button> is invalid
            // HTML. role/tabIndex/keyDown restore the missing semantics so
            // keyboard users can reach and trigger it; the design-system
            // focus halo applies via `_focusVisible` on the Button theme.
            <MButton
              as="span"
              role="button"
              tabIndex={0}
              aria-label={`Clear ${title} filter`}
              variant="tertiary"
              size="xs"
              fontSize="xxs"
              _hover={{ background: 'none' }}
              h="4"
              onClick={(event) => {
                event.stopPropagation();
                handleClearFilter?.();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  handleClearFilter?.();
                }
              }}
            >
              Clear
            </MButton>
          )}
          <Icon as={isExpanded ? MdArrowDropUp : MdArrowDropDown} />
        </Flex>
      </Accordion.ItemTrigger>

      {/* MAccordionPanel puts the padding on the padded body, as v2's
          `AccordionPanel p={1}` did; on ItemContent it stacked on the
          body's own. */}
      <MAccordionPanel p={1} m="0">
        {children}
      </MAccordionPanel>
    </>
  );
};

export const FilterCardAccordionItem = ({
  value,
  title,
  filterApplied = false,
  handleClearFilter,
  children,
}: FilterCardAccordionItemBodyProps & { value: string }) => (
  <Accordion.Item border="none" value={value}>
    <FilterCardAccordionItemBody
      title={title}
      filterApplied={filterApplied}
      handleClearFilter={handleClearFilter}
    >
      {children}
    </FilterCardAccordionItemBody>
  </Accordion.Item>
);

export const FilterCardCheckGroup = ({
  items,
  value,
  isRadio = false,
  returnObject = false,
  objectId = 'id',
  itemLabel = 'label',
  itemValue = 'value',
  onChange,
}: {
  items: any;
  value: any[] | any;
  // work as radio not checkboxes
  isRadio?: boolean;
  // whether to send object on onChange
  returnObject?: boolean;
  // when returnObject is true, objectId indicated the uniqueness of each object
  objectId?: string;
  itemLabel?: string;
  itemValue?: string;
  onChange: (val: any) => void;
}) => {
  const internalItemValue = itemValue || 'value';

  const isSelected = (item: any) => {
    if (isRadio) {
      return returnObject
        ? compareItems(item, value)
        : valueOfItem(item) === value;
    }
    return Array.isArray(value) && value.includes(valueOfItem(item));
  };

  const compareItems = (item1: any, item2: any) =>
    item1 &&
    item2 &&
    (returnObject
      ? item1[objectId] === item2[objectId]
      : item1[internalItemValue] === item2[internalItemValue]);

  const valueOfItem = (item: any) =>
    returnObject ? item : item[internalItemValue];

  const handleChange = (item: any) => {
    if (isRadio) {
      // Allows de-selection of radio button
      onChange(isSelected(item) ? {} : valueOfItem(item));
    } else {
      const selected = isSelected(item);
      const cVal = value || [];
      const newValue = valueOfItem(item);
      const selectingAll: boolean = newValue === '';
      let newItems = [];

      if (selected) {
        newItems = cVal.filter((val: any) => newValue !== val);
      } else {
        newItems = cVal
          .concat(newValue)
          // All option should not be selected with other filters.
          // Filter it out here or the other options depending on what is seleted.
          .filter((v: string) => (selectingAll ? !v : v));
      }

      onChange(newItems);
    }
  };

  return (
    <>
      {items.map((item: any, index: any) => (
        <Box key={index}>
          <MCheckbox
            my={1}
            onChange={() => handleChange(item)}
            isChecked={(isSelected(item) as any) || false}
          >
            <MText ml="1" fontWeight="medium">
              {item[itemLabel]}
            </MText>
          </MCheckbox>
        </Box>
      ))}
    </>
  );
};

export const FilterCardCheckGroupTest = ({
  items,
  onChangeCheckBox,
  onResetFilter,
}: {
  items: any[];
  onChangeCheckBox: (val: any) => void;
  onResetFilter: (val: any) => void;
}) => (
  <>
    {items.map((item, index) => (
      <Box key={index}>
        <MCheckbox
          my={1}
          onChange={() => onChangeCheckBox(item)}
          isChecked={item.checked}
        >
          <MText ml="1" fontWeight="medium">
            {item.label}
          </MText>
        </MCheckbox>
      </Box>
    ))}
  </>
);

export default MFilterCard;
